// /////////////////////////////////////////////////////////////
// [ファイル] AgentRail.tsx
// [概要] エージェント設定ダイアログの左レール。定義の区分 (ルール / スキル / ワークフロー / 課題)、
//        グループ一覧 (共通 + 各グループ。行をドロップすると所属を付け替える)、自動処理、
//        最下部に「AI へ渡す文字数」のメーター (グループなし = 共通のみ / 各グループ = 共通 + グループ) を置く。
// /////////////////////////////////////////////////////////////
import React from 'react';
import { Box, ButtonBase, IconButton, Tooltip, Typography } from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import { useDroppable } from '@dnd-kit/core';
import { motion } from 'framer-motion';
import * as Icons from '../../constants/icons';
import type { AgentGroup, AgentType } from '../../hooks/useAssistantAgents';
import { AGENT_TYPES, INJECTION_LIMIT, KIND_LABEL, type Section } from './agentSettingsShared';

export interface MeterRow {
  key: string; // '' = グループなしのチャット
  label: string;
  common: number; // 共通の定義の文字数
  own: number; // そのグループの定義の文字数
}

interface Props {
  section: Section;
  groupKey: string; // section='groups' のときの対象 ('' = 共通, NEW_GROUP_KEY = 作成中)
  newGroupKey: string;
  newGroupName: string | null; // 作成中のグループ名 (null = 作成していない)
  countByType: Record<AgentType, number>;
  groups: AgentGroup[];
  countByGroup: Map<string, number>; // '' も含む
  meterRows: MeterRow[];
  basePromptChars: number | null;
  dragging: boolean; // 行をドラッグ中 (グループ行にドロップ可能の見た目を出す)
  onNav: (section: Section, groupKey?: string) => void;
  onNewGroup: () => void;
}

const NAV_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  width: '100%',
  textAlign: 'left',
  px: 1.125,
  py: 0.875,
  borderRadius: 2,
  position: 'relative',
  fontSize: '0.8rem',
  transition: 'background .15s',
  justifyContent: 'flex-start',
  '&:hover': { bgcolor: 'rgba(0,0,0,.04)' },
  '& .MuiSvgIcon-root': { fontSize: 16, color: blueGrey[300], flexShrink: 0 },
  '&[aria-current="true"]': { bgcolor: 'action.selected', fontWeight: 600 },
  '&[aria-current="true"] .MuiSvgIcon-root': { color: blueGrey[600] },
  '&[aria-current="true"]::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: 7,
    bottom: 7,
    width: 3,
    borderRadius: 1,
    bgcolor: blueGrey[500],
  },
} as const;

const COUNT_SX = {
  ml: 'auto',
  fontSize: '0.68rem',
  color: 'text.secondary',
  bgcolor: '#fff',
  border: 1,
  borderColor: 'divider',
  borderRadius: 999,
  px: 0.875,
  fontVariantNumeric: 'tabular-nums',
  flexShrink: 0,
} as const;

function Eyebrow({ children, action }: { children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', px: 1, pt: 1.25, pb: 0.25 }}>
      <Typography
        variant="caption"
        sx={{ fontSize: '0.66rem', fontWeight: 700, letterSpacing: '.06em', color: 'text.secondary', flex: 1 }}
      >
        {children}
      </Typography>
      {action}
    </Box>
  );
}

const TYPE_ICON: Record<AgentType, React.ReactNode> = {
  rule: <Icons.Gavel />,
  skill: <Icons.AutoAwesome />,
  workflow: <Icons.AccountTree />,
  issue: <Icons.Task />,
};

/** グループ行 (ドロップ先)。共通 ('') も 1 行として出す */
function GroupNavButton({
  id,
  label,
  count,
  current,
  shared,
  dragging,
  onClick,
}: {
  id: string;
  label: string;
  count: number;
  current: boolean;
  shared: boolean;
  dragging: boolean;
  onClick: () => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `group:${id}` });
  return (
    <ButtonBase
      ref={setNodeRef}
      onClick={onClick}
      aria-current={current}
      sx={{
        ...NAV_SX,
        pl: 1.75,
        fontSize: '0.78rem',
        ...(dragging ? { outline: '1px dashed', outlineColor: 'divider', outlineOffset: -1 } : {}),
        ...(isOver ? { bgcolor: '#e3f2fd !important', outline: '2px dashed', outlineColor: 'primary.main', outlineOffset: -2 } : {}),
      }}
    >
      {id === '' ? <Icons.FolderOpen /> : shared ? <Icons.Groups /> : <Icons.Folder />}
      <Box component="span" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}
      </Box>
      <Box component="span" sx={COUNT_SX}>
        {count}
      </Box>
    </ButtonBase>
  );
}

export default function AgentRail({
  section,
  groupKey,
  newGroupKey,
  newGroupName,
  countByType,
  groups,
  countByGroup,
  meterRows,
  basePromptChars,
  dragging,
  onNav,
  onNewGroup,
}: Props) {
  return (
    <Box
      sx={{
        borderRight: 1,
        borderColor: 'divider',
        bgcolor: '#fafafa',
        px: 1,
        pt: 1,
        pb: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.25,
        overflowY: 'auto',
        minHeight: 0,
        height: '100%',
        // CssBaseline 未使用のため既定は content-box。高さ 100% に上下の余白が足されて枠からはみ出すのを防ぐ
        boxSizing: 'border-box',
      }}
    >
      <Eyebrow>定義</Eyebrow>
      {AGENT_TYPES.map((t) => (
        <ButtonBase key={t} onClick={() => onNav(t)} aria-current={section === t} sx={NAV_SX}>
          {TYPE_ICON[t]}
          <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
            {KIND_LABEL[t]}
          </Box>
          <Box component="span" sx={COUNT_SX}>
            {countByType[t]}
          </Box>
        </ButtonBase>
      ))}

      <Eyebrow
        action={
          <Tooltip title="新しいグループ">
            <IconButton size="small" onClick={onNewGroup} sx={{ p: 0.25 }}>
              <Icons.Add sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        }
      >
        グループ
      </Eyebrow>
      <GroupNavButton
        id=""
        label="共通"
        count={countByGroup.get('') ?? 0}
        current={section === 'groups' && groupKey === ''}
        shared={false}
        dragging={dragging}
        onClick={() => onNav('groups', '')}
      />
      {groups.map((g) => (
        <GroupNavButton
          key={g.id}
          id={g.id}
          label={g.name || '(名称未設定)'}
          count={countByGroup.get(g.id) ?? 0}
          current={section === 'groups' && groupKey === g.id}
          shared={g.scope === 'shared'}
          dragging={dragging}
          onClick={() => onNav('groups', g.id)}
        />
      ))}
      {newGroupName !== null && (
        <ButtonBase aria-current={section === 'groups' && groupKey === newGroupKey} sx={{ ...NAV_SX, pl: 1.75, fontSize: '0.78rem' }}>
          <Icons.Folder />
          <Box component="span" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontStyle: newGroupName ? 'normal' : 'italic' }}>
            {newGroupName || '(新しいグループ)'}
          </Box>
        </ButtonBase>
      )}

      <Eyebrow>自動化</Eyebrow>
      <ButtonBase onClick={() => onNav('automation')} aria-current={section === 'automation'} sx={NAV_SX}>
        <Icons.Bolt />
        <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
          メール自動処理
        </Box>
      </ButtonBase>

      {/* AI へ渡す文字数 (回答精度に関わるため常時表示)。行クリックでそのグループ面へ */}
      <Box sx={{ mt: 'auto', pt: 1.5 }}>
        <Eyebrow>AI へ渡す文字数</Eyebrow>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          {meterRows.map((r) => {
            const total = r.common + r.own;
            const warn = total > INJECTION_LIMIT;
            const cw = Math.min(100, (r.common / INJECTION_LIMIT) * 100);
            const gw = Math.min(100 - cw, (r.own / INJECTION_LIMIT) * 100);
            return (
              <Tooltip
                key={r.key}
                placement="right"
                title={
                  r.key
                    ? `共通 ${r.common.toLocaleString()} 字 + ${r.label} ${r.own.toLocaleString()} 字 = ${total.toLocaleString()} 字`
                    : `共通のみ ${r.common.toLocaleString()} 字 (グループを選ばないチャットで渡る量)`
                }
              >
                <ButtonBase
                  onClick={() => onNav('groups', r.key)}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    gap: 0.375,
                    borderRadius: 1.5,
                    px: 1,
                    py: 0.5,
                    width: '100%',
                    textAlign: 'left',
                    '&:hover': { bgcolor: 'rgba(0,0,0,.04)' },
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 0.75, fontSize: '0.68rem', minWidth: 0 }}>
                    <Box component="b" sx={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                      {r.label}
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        color: warn ? 'warning.main' : 'text.secondary',
                        fontWeight: warn ? 700 : 400,
                        fontVariantNumeric: 'tabular-nums',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {total.toLocaleString()}
                    </Box>
                  </Box>
                  <Box sx={{ height: 7, borderRadius: 1, bgcolor: '#fff', border: 1, borderColor: 'divider', overflow: 'hidden', display: 'flex' }}>
                    <Box
                      component={motion.i}
                      animate={{ width: `${cw}%` }}
                      transition={{ duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
                      sx={{ display: 'block', height: '100%', bgcolor: warn ? 'warning.main' : blueGrey[500] }}
                    />
                    <Box
                      component={motion.i}
                      animate={{ width: `${gw}%` }}
                      transition={{ duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
                      sx={{ display: 'block', height: '100%', bgcolor: warn ? 'warning.light' : 'primary.main' }}
                    />
                  </Box>
                </ButtonBase>
              </Tooltip>
            );
          })}
        </Box>
        {/* 凡例。各語を nowrap の span にまとめ、幅が足りない時は語の途中でなく項目単位で折り返す */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 0.75, rowGap: 0.25, px: 1, pt: 0.5, fontSize: '0.62rem', color: 'text.secondary' }}>
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}>
            <Box component="i" sx={{ width: 8, height: 8, borderRadius: 0.5, bgcolor: blueGrey[500], display: 'inline-block' }} />
            共通
          </Box>
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}>
            <Box component="i" sx={{ width: 8, height: 8, borderRadius: 0.5, bgcolor: 'primary.main', display: 'inline-block' }} />
            グループ
          </Box>
          <Box component="span" sx={{ ml: 'auto', whiteSpace: 'nowrap' }}>
            上限 {INJECTION_LIMIT.toLocaleString()} 字
          </Box>
        </Box>
        <Typography variant="caption" sx={{ display: 'block', px: 1, pt: 0.25, pb: 0.5, fontSize: '0.62rem', color: 'text.secondary', lineHeight: 1.4 }}>
          基本プロンプト{basePromptChars !== null ? ` (約 ${basePromptChars.toLocaleString()} 字)` : ''}は別枠で常に送られます
        </Typography>
      </Box>
    </Box>
  );
}
